import {useMemo} from 'react'

import type {MemexProjectColumnId} from '../../../api/columns/contracts/memex-column'
import {normalizeToLowercaseFieldName, splitFieldFilters} from '../../../components/filter-bar/helpers/search-filter'
import {useSearch} from '../../../components/filter-bar/search-context'
import type {ColumnModel} from '../../../models/column-model'
import {isIterationColumnModel} from '../../../models/column-model/guards'
import type {VerticalGroup} from '../../../models/vertical-group'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'
import {groupsFromIncludedAndExcludedFilters} from '../groups-from-included-and-excluded-filters'
/**
 * Builds the list of groups for the provided column id.
 * If the column id is not valid, undefined will be returned for the groups.
 * If a group is specifically included in the current search query, only those included will be returned.
 * If a group is excluded from the current search query, it will not be returned.
 *
 * @param groupedByColumnId The column id to generate groups for.
 * @param getGroupsForField A function that knows how to generate groups for a given field type.
 * @returns An array of groups or undefined as well as the ColumnModel for the groupedByColumnId.
 */
export const useFilteredGroups = (
  groupedByColumnId: MemexProjectColumnId | undefined,
  getGroupsForField: (
    columnModel?: ColumnModel,
    opts?: {includeAllCompletedIterations?: boolean},
  ) => Array<VerticalGroup>,
) => {
  const {orderedTokenizedFilters} = useSearch()
  const {allColumns} = useAllColumns()

  const groupByField = allColumns.find(c => c.id === groupedByColumnId)

  const groupByFieldOptions = useMemo(() => {
    if (!groupByField) {
      return undefined
    }
    const groupByFilter = orderedTokenizedFilters.reduce(
      (acc, filter) => {
        if (
          groupByField &&
          filter.type === 'field' &&
          allColumns.find(c => c.name.toLowerCase() === normalizeToLowercaseFieldName(filter.field)) === groupByField
        ) {
          for (const field of splitFieldFilters(filter.value.toLowerCase())) {
            acc[filter.exclude ? 'exclude' : 'include'].add(field)
          }
        }
        return acc
      },
      {include: new Set<string>(), exclude: new Set<string>()},
    )

    const groups = getGroupsForField(groupByField, {
      includeAllCompletedIterations:
        isIterationColumnModel(groupByField) && groupByFilter.include.size + groupByFilter.exclude.size > 0,
    })

    return groupsFromIncludedAndExcludedFilters(groupByField, groups, groupByFilter)
  }, [groupByField, orderedTokenizedFilters, getGroupsForField, allColumns])

  return {groupByFieldOptions, groupByField}
}
